'use client';

import { useState, FormEvent } from 'react';
import { useAuth, User } from '../context/AuthContext';
import { clienteApi } from '@/services/clienteApi';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import { useGoogleLogin, TokenResponse } from '@react-oauth/google';
import { mensajeErrorGoogle } from '@/lib/auth/erroresInicioGoogle';
import { destinoDesdeBusqueda } from '@/lib/auth/destinoTrasLogin';

interface LoginResponse {
    token: string;
    usuario: User;
    message?: string;
    code?: string;
    mfaRequired?: boolean;
    tempToken?: string;
}

/**
 * A dónde se manda al usuario recién autenticado.
 *
 * Una cuenta sin verificar va primero a verificar el email. Después, si el login
 * se abrió con `?volver=` (un enlace profundo que caducó la sesión), se vuelve a
 * ese enlace; el layout del panel ya reencamina a /app/subscribe a quien no
 * pueda usarlo. Sin `volver`, manda el rol y la suscripción como siempre.
 *
 * `volver` se lee de `window.location` al enviar, no con `useSearchParams`, que
 * obligaría a una frontera de Suspense en la página de login.
 */
function destinoTrasLogin(usuario: User, emailTecleado: string, comprobarVerificacion: boolean): string {
    if (comprobarVerificacion && !usuario?.isVerified) {
        return `/verify-email?email=${encodeURIComponent(usuario?.email || emailTecleado)}`;
    }

    const destinoPedido = destinoDesdeBusqueda(window.location.search);
    if (destinoPedido) {
        return destinoPedido;
    }

    if (usuario?.role === 'admin' || usuario?.role === 'superadmin') {
        return '/app/admin/users';
    }

    return ['active', 'trialing'].includes(usuario?.subscriptionStatus) ? '/app/dashboard/patients' : '/app/subscribe';
}

export default function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    
    // --- MFA State ---
    const [mfaRequired, setMfaRequired] = useState(false);
    const [mfaCode, setMfaCode] = useState('');

    const { login } = useAuth();
    const router = useRouter();

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            const res = await clienteApi.crudo('/api/auth/login', {
                method: 'POST',
                cuerpo: { email, password },
            });

            const data: LoginResponse = await res.json();

            if (res.ok) {
                if (data.mfaRequired) {
                    setMfaRequired(true);
                    return; // Detenemos aquí para pedir MFA
                }

                login(data.usuario);
                router.push(destinoTrasLogin(data.usuario, email, true));
            } else {
                setError(data.message || 'Error al iniciar sesión');
            }
        } catch (err) {
            console.error('Login error:', err);
            setError('Error de conexión con el servidor');
        } finally {
            setIsLoading(false);
        }
    };

    const handleMfaSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            const res = await clienteApi.crudo('/api/auth/login/mfa', {
                method: 'POST',
                // Sin `tempToken`: desde la etapa 3 no viaja en el JSON, así que el
                // cliente no lo tiene. El backend lo saca de la cookie de MFA pendiente
                // que emitió `/login`.
                cuerpo: { code: mfaCode },
            });

            const data: LoginResponse = await res.json();

            if (res.ok) {
                login(data.usuario);
                router.push(destinoTrasLogin(data.usuario, email, true));
            } else {
                setError(data.message || 'Código incorrecto');
            }
        } catch (err) {
            console.error('MFA error:', err);
            setError('Error comprobando el código MFA');
        } finally {
            setIsLoading(false);
        }
    };

    const handleGoogleLogin = useGoogleLogin({
        onSuccess: async (tokenResponse: TokenResponse) => {
            setIsLoading(true);
            setError('');
            try {
                const res = await clienteApi.crudo('/api/auth/google', {
                    method: 'POST',
                    cuerpo: { token: tokenResponse.access_token },
                });
                const data: LoginResponse = await res.json();
                if (res.ok) {
                    if (data.mfaRequired) {
                        setMfaRequired(true);
                            return; // Detenemos para pedir MFA
                    }

                    login(data.usuario);
                    router.push(destinoTrasLogin(data.usuario, '', false));
                } else {
                    setError(mensajeErrorGoogle(res.status, data, 'Error al iniciar sesión con Google'));
                }
            } catch {
                setError('Error al iniciar sesión. Inténtalo de nuevo.');
            } finally {
                setIsLoading(false);
            }
        },
        onError: () => setError('Falló el inicio de sesión con Google'),
    });

    return (
        <div className="flex min-h-screen bg-canvas landing-grid flex-col items-center justify-center p-4">

            {/* --- LOGO GIGANTE (Ancho del Formulario) --- */}
            <Link
                href="/"
                className="mb-8 w-full max-w-md transition-transform hover:scale-105 hover:opacity-90 flex justify-center"
            >
                <div className="relative w-full h-48"> {/* h-48 (192px) permite que el logo se expanda */}
                    <Image
                        src="/THERAPY(2).svg"
                        alt="TherapyRecap Logo"
                        fill
                        className="object-contain dark:invert" // Ajusta al contenedor sin deformarse
                        priority
                    />
                </div>
            </Link>
            {/* ------------------------------------------- */}

            <div className="w-full max-w-md bg-surface-2 p-8 rounded-3xl shadow-[8px_8px_0px_0px_var(--shadow-color)] border-2 border-ink">
                <div className="text-center mb-8">
                    <h1 className="text-2xl font-bold text-ink mb-2">
                        {mfaRequired ? 'Verificación de Seguridad' : 'Bienvenido de nuevo'}
                    </h1>
                    <p className="text-ink/80 text-sm font-medium">
                        {mfaRequired 
                            ? 'Introduce el código de 6 dígitos generado por tu aplicación autenticadora.' 
                            : 'Accede a tu cuenta para gestionar pacientes'}
                    </p>
                </div>

                {mfaRequired ? (
                    <form onSubmit={handleMfaSubmit} className="space-y-5">
                        {error && (
                            <div className="p-3 bg-tint-danger-strong border-2 border-danger text-on-tint-danger text-sm rounded-xl font-bold text-center shadow-[4px_4px_0px_0px_#ef4444]">
                                {error}
                            </div>
                        )}
                        <div>
                            <label className="block text-xs font-bold text-ink mb-1.5 uppercase tracking-wider text-center">
                                Código TOTP
                            </label>
                            <input
                                type="text" 
                                required 
                                value={mfaCode} 
                                onChange={(e) => setMfaCode(e.target.value.replace(/[^0-9]/g, ''))}
                                maxLength={6}
                                className="w-full bg-surface-2 border-2 border-ink text-ink rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-ink/20 focus:border-ink transition-all placeholder:text-ink/40 font-bold text-center text-2xl tracking-[0.5em]"
                                placeholder="000000"
                            />
                        </div>
                        <button type="submit" disabled={isLoading || mfaCode.length !== 6} className="w-full bg-ink text-canvas border-2 border-ink hover:bg-ink/90 font-bold py-3 rounded-xl shadow-[4px_4px_0px_0px_var(--shadow-color)] hover:translate-y-0.5 hover:shadow-[2px_2px_0px_0px_var(--shadow-color)] active:translate-y-1 active:shadow-none transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center">
                            {isLoading ? <span className="material-symbols-outlined animate-spin text-xl" aria-hidden="true">progress_activity</span> : 'Verificar e Iniciar Sesión'}
                        </button>
                    </form>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-5">
                        {error && (
                            <div className="p-3 bg-tint-danger-strong border-2 border-danger text-on-tint-danger text-sm rounded-xl font-bold text-center shadow-[4px_4px_0px_0px_#ef4444]">
                                {error}
                            </div>
                        )}
                        <div>
                            <label className="block text-xs font-bold text-ink mb-1.5 uppercase tracking-wider">
                                Email Profesional
                            </label>
                            <input
                                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                                className="w-full bg-surface-2 border-2 border-ink text-ink rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-ink/20 focus:border-ink transition-all placeholder:text-ink/40 font-medium"
                                placeholder="tu@email.com"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-ink mb-1.5 uppercase tracking-wider">
                                Contraseña
                            </label>
                            <input
                                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                                className="w-full bg-surface-2 border-2 border-ink text-ink rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-ink/20 focus:border-ink transition-all placeholder:text-ink/40 font-medium"
                                placeholder="••••••••"
                            />
                        </div>
                        <div className="flex justify-end">
                            <Link href="/auth/forgot-password" className="text-xs font-bold text-ink hover:underline transition-colors">
                                ¿Olvidaste tu contraseña?
                            </Link>
                        </div>
                        <button type="submit" disabled={isLoading} className="w-full bg-ink text-canvas border-2 border-ink hover:bg-ink/90 font-bold py-3 rounded-xl shadow-[4px_4px_0px_0px_var(--shadow-color)] hover:translate-y-0.5 hover:shadow-[2px_2px_0px_0px_var(--shadow-color)] active:translate-y-1 active:shadow-none transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center">
                            {isLoading ? <span className="material-symbols-outlined animate-spin text-xl" aria-hidden="true">progress_activity</span> : 'Iniciar Sesión'}
                        </button>
                    </form>
                )}

                <div className="relative my-8">
                    <div className="absolute inset-0 flex items-center"><div className="w-full border-t-2 border-ink/20"></div></div>
                    <div className="relative flex justify-center text-sm"><span className="px-4 bg-surface-2 text-ink/60 font-medium">O continúa con</span></div>
                </div>

                <button type="button" onClick={() => handleGoogleLogin()} disabled={isLoading} className="w-full flex items-center justify-center gap-3 bg-surface-2 hover:bg-tint-neutral border-2 border-ink text-ink font-bold py-3 rounded-xl shadow-[4px_4px_0px_0px_var(--shadow-color)] hover:translate-y-0.5 hover:shadow-[2px_2px_0px_0px_var(--shadow-color)] active:translate-y-1 active:shadow-none transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                    </svg>
                    Google
                </button>

                <div className="mt-8 text-center text-sm text-ink/80 font-medium">
                    ¿No tienes cuenta? <Link href="/app/register" className="text-ink font-bold hover:underline transition-all">Regístrate gratis</Link>
                </div>
            </div>
        </div>
    );
}
